import SAPOMailConfig from "@/SAPOMailConfig";
import ClientLoggingItem from "./Entities/ClientLoggingItem";
import ClientLoggingHTTPService from './HTTP/ClientLoggingHTTPService';

export {ClientLoggingControllerSingleton as default};

/**
 * @description Controller responsible for managing
 * Logging JS
 *
 * @author rsommerfeld <roni.s.sommerfeld@altice.pt>
 * @since 7.0.0
 * @private
 */
class ClientLoggingController {
  /**
   * Logging JS HTTP Service
   *
   * @private
   *
   * @since 7.0.0
   *
   * @type {ClientLoggingHTTPService}
   */
  http_service = new ClientLoggingHTTPService();

  /**
   * Maximum number of entries to keep in memory
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {Number}
   */
  max_entries = 10;

  /**
   * Entries in memory
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {Array}
   */
  entries = [];

  /**
   * Level of errors or warnings
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {Object.<String, String>}
   */
  static LEVELS = {
    ERROR: 'error',
    WARNING: 'warning'
  }

  /**
   * Method that handles errors that are caught
   * in the Vue application
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Object} err
   * @param {Object} instance
   * @param {String} info
   *
   * @returns {void}
   */
  onVueError(err, instance, info){
    const item = this.mapItemFromVue(err, ClientLoggingController.LEVELS.ERROR);
    this.createLogError(item);
  }

  /**
   * Method that handles warnings that are caught
   * in the Vue application
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Object} warn
   * @param {Object} instance
   * @param {String} info
   *
   * @returns {void}
   */
  onVueWarn(warn, instance, info){
    const item = this.mapItemFromVue(warn,  ClientLoggingController.LEVELS.WARNING);
    this.createLogWarn(item);
  }

   /**
   * Create a log event in the backend
   * when an error occurs in the frontend involving
   * javascript
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ClientLoggingItem} item
   *
   * @return {Promise<Boolean>}
   */
  async createLogError(item) {
    let response = await this.http_service.createLogError(item);
    this.clearEntries();
    return response.success;
  }

  /**
   * Create a log event in the backend
   * when an warning occurs in the frontend involving
   * javascript/vue
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ClientLoggingItem} item
   *
   * @return {Promise<Boolean>}
   */
  async createLogWarn(item) {
    let response = await this.http_service.createLogWarn(item);
    this.clearEntries();
    return response.success;
  }

  /**
   * Adds an entry to the in-memory log
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Object} data
   *
   * @return {void}
   */
  addEntry(data) {
    this.entries.push(data);

    if (this.entries.length > this.max_entries) {
      this.entries.shift();
    }
  }

  clearEntries() {
    this.entries = [];
  }

  getEntries() {
    return this.entries;
  }

  /**
   * Maps the fields originating from the application
   * from VUE
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {Object} data
   *
   * @returns {ClientLoggingItem}
   */
  mapItemFromVue(data, level){
    const item       = new ClientLoggingItem;
    item.app_version = SAPOMailConfig.client.version.js;
    item.user_agent  = navigator ? navigator.userAgent : 'noNavigator';
    item.url_path    = location.href;
    item.level       = level;
    item.stack       = data.stack || '';
    item.type        = data.name || 'warning';
    item.message     = data.message || data;
    item.entries     = this.entries;
    return item;
  }
}

const ClientLoggingControllerSingleton = new ClientLoggingController();